import React, { useEffect, useState } from 'react';
import api from '../api';

// Aaj di date (local) YYYY-MM-DD
const todayStr = () => {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmt = v => (v ? new Date(v).toLocaleString('en-IN') : '—');

// Stage template de naam ton: klickson_reminder4_second -> Second
const STAGES = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
const stageOf = r => {
  const m = String(r.template_name || '').toLowerCase().match(/_(first|second|third|fourth|fifth)$/);
  if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return r.reminder_stage || '—';
};

const BADGE = {
  sent:     { bg: '#dbeafe', color: '#1d4ed8', label: 'Sent' },
  delivered:{ bg: '#dcfce7', color: '#15803d', label: 'Delivered' },
  read:     { bg: '#f3e8ff', color: '#7e22ce', label: 'Read' },
  failed:   { bg: '#fee2e2', color: '#b91c1c', label: 'Failed' },
  not_sent: { bg: '#ffedd5', color: '#c2410c', label: 'Not Sent' },
  pending:  { bg: '#fef9c3', color: '#a16207', label: 'Pending' },
};

function StatusBadge({ status }) {
  const b = BADGE[status] || { bg: '#eee', color: '#555', label: status || '—' };
  return (
    <span style={{ background: b.bg, color: b.color, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
      {b.label}
    </span>
  );
}

export default function ReminderLogs() {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [filter, setFilter] = useState('all');
  const [stage, setStage] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get('/messages/reminder-logs', { params: { date } })
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [date]);

  // ---- Daily summary ----
  const count = fn => rows.filter(fn).length;
  const summary = [
    { key: 'all',       label: 'Total Reminders',     value: rows.length,                                              color: '#334155' },
    { key: 'sentany',   label: 'Sent to WhatsApp',    value: count(r => ['sent','delivered','read'].includes(r.status)), color: '#1d4ed8' },
    { key: 'delivered', label: 'Delivered',           value: count(r => ['delivered','read'].includes(r.status)),       color: '#15803d' },
    { key: 'read',      label: 'Read',                value: count(r => r.status === 'read'),                           color: '#7e22ce' },
    { key: 'sent',      label: 'Not Delivered Yet',   value: count(r => r.status === 'sent'),                           color: '#a16207' },
    { key: 'problem',   label: 'Failed / Not Sent',   value: count(r => ['failed','not_sent','pending'].includes(r.status)), color: '#b91c1c' },
  ];

  const matchFilter = r => {
    switch (filter) {
      case 'all': return true;
      case 'sentany': return ['sent', 'delivered', 'read'].includes(r.status);
      case 'delivered': return ['delivered', 'read'].includes(r.status);
      case 'problem': return ['failed', 'not_sent', 'pending'].includes(r.status);
      default: return r.status === filter;
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r => {
    if (!matchFilter(r)) return false;
    if (stage !== 'all' && stageOf(r) !== stage) return false;
    if (!q) return true;
    return [r.phone, stageOf(r), r.reason, r.template_name]
      .some(v => String(v || '').toLowerCase().includes(q));
  });

  const reasonText = r => {
    if (r.reason) return r.reason;
    if (r.status === 'failed') return 'WhatsApp delivery failed';
    if (r.status === 'sent') return 'Sent, delivery confirmation awaited';
    return '—';
  };

  const input = { padding: '7px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 };

  return (
    <div>
      {/* Summary cards (click karke filter karo) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
        {summary.map(s => (
          <div key={s.key} onClick={() => setFilter(s.key)}
            style={{
              background: '#fff', borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
              border: filter === s.key ? `2px solid ${s.color}` : '1px solid #e5e7eb',
            }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="box">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ marginBottom: 0 }}>Reminder Logs</h3>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={input} />
          <input placeholder="Search phone, stage or reason..." value={search}
            onChange={e => setSearch(e.target.value)} style={{ ...input, width: 280 }} />
          <select value={filter} onChange={e => setFilter(e.target.value)} style={input}>
            <option value="all">All Statuses</option>
            <option value="sent">Sent (not delivered yet)</option>
            <option value="delivered">Delivered</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
            <option value="not_sent">Not Sent</option>
            <option value="pending">Pending</option>
            <option value="problem">Failed / Not Sent / Pending</option>
          </select>
          <select value={stage} onChange={e => setStage(e.target.value)} style={input}>
            <option value="all">All Reminder Stages</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#888' }}>{filtered.length} records</span>
          <span style={{ fontSize: 12, color: '#aaa' }}>{loading ? 'Refreshing…' : 'Auto refresh 30s'}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Phone</th>
                <th>Stage</th>
                <th>Template</th>
                <th>Status</th>
                <th>Delivered At</th>
                <th>Read At</th>
                <th>Reason / Note</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
                  Is date layi koi reminder log nahi
                </td></tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td>{r.phone || '—'}</td>
                  <td>{stageOf(r)}</td>
                  <td style={{ fontSize: 11, color: '#666' }}>{r.template_name || '—'}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{fmt(r.delivered_at)}</td>
                  <td>{fmt(r.read_at)}</td>
                  <td style={{ fontSize: 12, color: ['failed', 'not_sent'].includes(r.status) ? '#b91c1c' : '#666', maxWidth: 260 }}>
                    {reasonText(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}